import { animate, style, transition, trigger } from '@angular/animations';
import { Component, computed, inject, input, OnChanges, OnDestroy, output, signal, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import { PageModel } from '@empathy/survey-core';

import { QuestionControl } from '../../models/page-control.interface';
import { SurveyViewerStateService } from '../../services/survey-viewer-state.service';
import { SurveyViewerService } from '../../services/survey-viewer.service';

@Component({
  selector: 'empathy-page-view',
  templateUrl: 'page-view.component.html',
  styleUrls: ['./page-view.component.scss', './_page-view-theme.component.scss'],
  animations: [trigger('showFooter', [transition(':enter', [style({ opacity: 0 }), animate('250ms cubic-bezier(.17,.67,1,1.04)')])])]
})
export class PageViewComponent implements OnChanges, OnDestroy {
  autoSubmitting = input<boolean>();
  submitting = input<boolean>(false);
  isSaving = input<boolean>(false);
  page = input<PageModel>();
  progress = input<number>(0);
  questionCtrls = input<QuestionControl>();
  showCompleteButton = input<boolean>();

  httpError = output();
  next = output();
  prev = output();

  hasErrors$ = signal(false);
  forceStop$ = signal(false);
  notAllCtrlsValid$ = signal(false);
  shouldShowBackButton$ = computed(
    () => this.surveyViewerService.activePageIndex$() !== 0 && (this.surveyViewerService.isTestMode$() || !this.surveyViewerService.isBackButtonDisabled$())
  );

  nextButtonText$ = computed(() => {
    const remaining = this.surveyViewerStateService.remainingViewTime$();

    const text = this.showCompleteButton() ? 'Complete' : 'Next question';
    return remaining > 0 ? `${text} (${remaining}s)` : text;
  });

  surveyViewerService = inject(SurveyViewerService);
  surveyViewerStateService = inject(SurveyViewerStateService);

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;

  private subscriptions: Subscription[] = [];

  private get anyPageCtrlsInvalid(): boolean {
    return this.visibleControls.some((ctrl) => ctrl.invalid || ctrl.pending);
  }

  private get allPageControls(): Array<FormControl | FormArray | FormGroup> {
    const controls = [];
    const questionCtrls = this.questionCtrls();

    if (!questionCtrls) {
      return controls;
    }

    for (const qId in questionCtrls) {
      if (Object.prototype.hasOwnProperty.call(questionCtrls, qId)) {
        controls.push(questionCtrls[qId]);
      }
    }

    return controls;
  }

  private get visibleControls(): Array<FormControl | FormArray | FormGroup> {
    const page = this.page();
    const questionCtrls = this.questionCtrls();

    if (!page || !questionCtrls) {
      return [];
    }

    return page.visibleQuestions.map((question) => questionCtrls[question.id]).filter((control) => !!control) as Array<FormControl | FormArray | FormGroup>;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.questionCtrls || changes.page) {
      if (this.subscriptions.length > 0) {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
      }
      this.syncValidationState();
      this.allPageControls.forEach((ctrl) => {
        this.subscriptions.push(
          ctrl.valueChanges.subscribe(() => {
            this.syncValidationState();
          })
        );

        this.subscriptions.push(
          ctrl.statusChanges.subscribe(() => {
            this.syncValidationState();
          })
        );
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  onNext(): void {
    this.visibleControls.forEach((ctrl) => ctrl.markAsTouched());
    this.syncValidationState();
    if (this.notAllCtrlsValid$()) {
      return;
    }

    this.next.emit();
  }

  onPrev(): void {
    this.forceStop$.set(false);
    this.prev.emit();
  }

  private syncValidationState(): void {
    this.hasErrors$.set(this.visibleControls.some((ctrl) => !!ctrl.errors));
    this.notAllCtrlsValid$.set(this.anyPageCtrlsInvalid);
  }
}
